import React, { useEffect, useRef } from 'react';

import { Formik } from 'formik';
import { Button, ErrorMessage, FormikDropdownField, FormikTextInput, StandardForm } from 'ui-kit';
import * as Yup from 'yup';

import { KeyListener } from '../../../hooks/useKeyListener/KeyListener';
import { useTourContext } from 'tours/TourProvider';

export interface FormValues {
  poolName: string;
  originationType: string;
}

export const MAX_POOL_NAME_LENGTH = 75;

const validationSchema = Yup.object().shape({
  poolName: Yup.string()
    .trim()
    .required('Pool name is required')
    .max(MAX_POOL_NAME_LENGTH, `Pool name must be ${MAX_POOL_NAME_LENGTH} characters or fewer`),
  originationType: Yup.string().required('Origination type is required'),
});

export interface CreatePoolFormProps {
  createPool: (formValues: FormValues) => Promise<unknown>;
  closeModal: () => void;
  initialPoolName?: string;
  /**
   * Override the default subheader copy. The vanilla flow uses "Name the
   * selected carve and save your Pool." Tape-driven entry points pass
   * something more contextual.
   */
  subheaderText?: string;
}

const originationTypeOptions = [
  {
    label: 'Direct',
    value: 'direct',
  },
  {
    label: 'Indirect',
    value: 'indirect',
  },
  {
    label: 'Indirect: Fintech',
    value: 'indirect_fintech',
  },
  {
    label: 'Mixed',
    value: 'mixed',
  },
  {
    label: 'Non Applicable',
    value: 'n_a',
  },
];

const CreatePoolForm: React.FC<CreatePoolFormProps> = ({
  createPool,
  closeModal,
  initialPoolName,
  subheaderText,
}) => {
  const { startTour, tourDataLoading } = useTourContext();
  const hasStartedTour = useRef(false);
  // In-flight guard against duplicate pool creation. createPool is async and
  // each call mints a pool/job, so a double-click or mashed Enter would create
  // one pool per submit. Latched true on success (the modal unmounts), released
  // only on failure for a legit retry — this also closes the race where a fast
  // sync-pool create finishes before a second queued submit's validation runs.
  const isCreatingRef = useRef(false);

  const initialValues = {
    poolName: initialPoolName ?? '',
    originationType: originationTypeOptions[0].value,
  };

  // Auto-start tour when modal opens (component mounts).
  // Suppressed when name is prefilled (tape-driven flow) — user already knows the action.
  useEffect(() => {
    if (initialPoolName) return;
    if (hasStartedTour.current || tourDataLoading) {
      return;
    }

    // Small delay to ensure the modal is fully rendered
    const timer = setTimeout(() => {
      const element = document.querySelector('[data-tour="create-pool-modal"]');
      if (element) {
        hasStartedTour.current = true;
        startTour('portfolio.loan-level-data.create-pool-modal');
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [tourDataLoading, startTour, initialPoolName]);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async (values) => {
        if (isCreatingRef.current) return;
        isCreatingRef.current = true;
        try {
          await createPool({
            ...values,
            poolName: values.poolName.trim(),
          });
          closeModal();
        } catch (err) {
          isCreatingRef.current = false;
          throw err;
        }
      }}
    >
      {({ submitForm, values, isSubmitting }) => (
        <KeyListener
          listenerData={[
            { key: 'Enter', action: submitForm },
            { key: 'Return', action: submitForm },
          ]}
        >
          <div data-tour="create-pool-modal" className={initialPoolName ? 'min-w-[480px]' : ''}>
            <StandardForm closeModal={closeModal}>
              <div className="mb-2 py-2 text-[18px] font-bold leading-6 text-white">
                Create A Pool
              </div>
              <div className="py-2 text-[14px] font-normal leading-5 text-slate-200">
                POOL CREATION
              </div>
              <div
                id="subheader"
                className="mb-2 py-2 text-[14px] font-normal leading-5 text-slate-200"
              >
                {subheaderText ?? 'Name the selected carve and save your Pool.'}
              </div>

              <div
                className={`mb-4 grid w-full gap-4 ${
                  initialPoolName ? 'grid-cols-[160px_1fr]' : 'grid-cols-2'
                }`}
              >
                <label htmlFor="poolName" className="flex items-center">
                  Create A Pool Name
                </label>
                <div className="min-w-0">
                  <FormikTextInput
                    name="poolName"
                    placeholder="Enter Name..."
                    size={'small' as 'small'}
                    variant="light"
                    containerClass="bg-gray-950"
                    maxLength={MAX_POOL_NAME_LENGTH}
                    onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        e.stopPropagation();
                        submitForm();
                      }
                    }}
                  />
                  <ErrorMessage>
                    {values.poolName.length === MAX_POOL_NAME_LENGTH
                      ? `Pool name has reached the ${MAX_POOL_NAME_LENGTH} character limit`
                      : null}
                  </ErrorMessage>
                </div>

                <label htmlFor="originationType" className="flex items-center">
                  Select Origination Type
                </label>

                <FormikDropdownField name="originationType" options={originationTypeOptions} />
              </div>
              <div className="mt-4 flex w-full justify-end py-4">
                <Button
                  buttonType="submit"
                  type="primary_light"
                  label={isSubmitting ? 'Creating…' : 'Create Pool'}
                  disabled={isSubmitting}
                  data-testid="create-pool-submit-button"
                >
                  {isSubmitting ? 'Creating…' : 'Create Pool'}
                </Button>
              </div>
            </StandardForm>
          </div>
        </KeyListener>
      )}
    </Formik>
  );
};

export default CreatePoolForm;
